// Footer with subscribe form, address, social, navigation
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin, Instagram, Twitter, Youtube, ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { postSubscribe } from "@/lib/api";

const Footer = () => {
  const { site, categories } = useSite();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await postSubscribe({ email });
      setStatus("ok");
      setEmail("");
    } catch (err) {
      setStatus("err");
    }
  };

  const social = site?.social || {};
  const socials = [
    { url: social.facebook, Icon: Facebook, label: "facebook" },
    { url: social.linkedin, Icon: Linkedin, label: "linkedin" },
    { url: social.instagram, Icon: Instagram, label: "instagram" },
    { url: social.twitter, Icon: Twitter, label: "twitter" },
    { url: social.youtube, Icon: Youtube, label: "youtube" },
  ].filter((s) => s.url && s.url !== "#");

  return (
    <footer data-testid="site-footer" className="bg-[var(--jt-ink)] text-white">
      {/* Big CTA strip */}
      <div className="bg-[var(--jt-primary)]">
        <div className="jt-container py-10 lg:py-14 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div>
            <div className="eyebrow !text-black/80">Bulk &nbsp;/ &nbsp;Export &nbsp;/ &nbsp;OEM</div>
            <h3 className="font-display font-black text-3xl lg:text-5xl uppercase tracking-tight leading-none mt-2">
              Tooling up the<br className="hidden sm:block"/> world's workshops.
            </h3>
          </div>
          <Link to="/contact" className="jt-btn jt-btn-ink self-start" data-testid="footer-cta-quote">
            Request Bulk Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="jt-container py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Brand */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-white p-1.5 inline-flex">
              <img
                src="/brand/jhalani-logo.png"
                alt="Jhalani Hand Tools"
                className="h-12 w-auto"
              />
            </div>
            <div>
              <div className="font-display font-bold">Jhalani Hand Tools</div>
              <div className="font-mono text-[0.6rem] tracking-widest text-white/60 uppercase">Since 1961</div>
            </div>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            India's trusted manufacturer of professional hand tools — spanners, pliers, hammers,
            screwdrivers, sockets and more. Drop-forged. Chrome-vanadium. Built to last.
          </p>
          <div className="mt-5 flex gap-2">
            {socials.map(({ url, Icon, label }) => (
              <a
                key={label}
                href={url.startsWith("//") ? `https:${url}` : url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label}`}
                className="w-9 h-9 flex items-center justify-center border border-white/20 hover:bg-[var(--jt-primary)] hover:border-[var(--jt-primary)] transition-colors"
              >
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div>
          <div className="eyebrow !text-white/60">Top Categories</div>
          <ul className="mt-3 space-y-2">
            {categories.slice(0, 7).map((c) => (
              <li key={c.slug}>
                <Link to={`/category/${c.slug}`} className="text-sm text-white/80 hover:text-[var(--jt-primary)]">
                  {c.title.split("Manufacturer")[0].split("Hand Tool")[0].trim() || c.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick links */}
        <div>
          <div className="eyebrow !text-white/60">Company</div>
          <ul className="mt-3 space-y-2">
            <li><Link className="text-sm text-white/80 hover:text-[var(--jt-primary)]" to="/about">Our Legacy</Link></li>
            <li><Link className="text-sm text-white/80 hover:text-[var(--jt-primary)]" to="/manufacturing">Manufacturing</Link></li>
            <li><Link className="text-sm text-white/80 hover:text-[var(--jt-primary)]" to="/dealers">Become a Dealer</Link></li>
            <li><Link className="text-sm text-white/80 hover:text-[var(--jt-primary)]" to="/blog">Blog</Link></li>
            <li><Link className="text-sm text-white/80 hover:text-[var(--jt-primary)]" to="/contact">Contact</Link></li>
          </ul>
        </div>

        {/* Newsletter + contact */}
        <div>
          <div className="eyebrow !text-white/60">Stay Updated</div>
          <p className="text-sm text-white/70 mt-2">New products, catalogue drops, expo invites.</p>
          <form onSubmit={onSubscribe} className="mt-3 flex" data-testid="subscribe-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="subscribe-input"
              className="flex-1 bg-white/5 border border-white/15 px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--jt-primary)]"
            />
            <button type="submit" data-testid="subscribe-submit" className="bg-[var(--jt-primary)] hover:bg-[var(--jt-primary-hover)] px-4 transition-colors">
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          {status === "ok" && <div className="mt-2 font-mono text-xs text-[var(--jt-primary)]" data-testid="subscribe-success">SUBSCRIBED</div>}
          {status === "err" && <div className="mt-2 font-mono text-xs text-red-400">Something went wrong</div>}

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[var(--jt-primary)]"/><span className="text-white/80">{site?.address || "A-147, Sector 83, Noida, UP, India"}</span></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[var(--jt-primary)]"/><a href={`tel:${site?.phone}`} className="text-white/80 hover:text-[var(--jt-primary)] font-mono">{site?.phone}</a></div>
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[var(--jt-primary)]"/><a href={`mailto:${site?.email}`} className="text-white/80 hover:text-[var(--jt-primary)] font-mono">{site?.email}</a></div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="jt-container py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50 font-mono">
          <div className="flex items-center gap-3">
            <div className="bg-[#c9ccd1] px-3 py-2 inline-flex items-center rounded-sm">
              <img src="/brand/ferreterro-logo.png" alt="Ferreterro Tools LLP" className="h-7 w-auto" />
            </div>
            <span>© 2015 Ferreterro Tools LLP</span>
          </div>
          <div>All rights reserved · jhalanitools.com · Drop-forged in India</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
